import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Calendar,
  Clock,
  ChevronLeft,
  ChevronRight,
  Video,
  CheckCircle2,
} from "lucide-react";
import {
  format,
  addDays,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  isSameDay,
  parseISO,
  isAfter,
  isBefore,
} from "date-fns";
import { toast } from "react-hot-toast";

export default function BookMeeting({ user }) {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState(null);
  const [config, setConfig] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [duration, setDuration] = useState("30");
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [booking, setBooking] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    zoom_link: "",
  });

  useEffect(() => {
    fetchTeacherInfo();
  }, [teacherId]);

  useEffect(() => {
    if (config) {
      fetchSlots();
    }
  }, [teacherId, duration, weekStart, config]);

  const fetchTeacherInfo = async () => {
    try {
      const [configRes] = await Promise.all([
        axios.get(`${API}/scheduling-config/${teacherId}`),
      ]);
      setConfig(configRes.data);
      setDuration(String(configRes.data.min_duration));

      // Try to get teacher info from users endpoint
      try {
        const teacherRes = await axios.get(`${API}/users/teachers`);
        const t = teacherRes.data.find((u) => u.user_id === teacherId);
        if (t) setTeacher(t);
      } catch {
        /* teacher info not critical */
      }
    } catch (err) {
      toast.error("Failed to load teacher info");
      navigate("/meetings");
    } finally {
      setLoading(false);
    }
  };

  const fetchSlots = async () => {
    try {
      const dateFrom = format(weekStart, "yyyy-MM-dd");
      const dateTo = format(endOfWeek(weekStart, { weekStartsOn: 1 }), "yyyy-MM-dd");
      const res = await axios.get(
        `${API}/available-slots/${teacherId}?duration=${duration}&date_from=${dateFrom}&date_to=${dateTo}`
      );
      setSlots(res.data.slots || []);
    } catch (err) {
      console.error("Error fetching slots:", err);
    }
  };

  // Group slots by date
  const slotsByDate = useMemo(() => {
    const map = {};
    slots.forEach((s) => {
      map[s.date] = map[s.date] || [];
      map[s.date].push(s);
    });
    return map;
  }, [slots]);

  // Generate week days
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(weekStart, i));
    }
    return days;
  }, [weekStart]);

  // Available dates this week
  const availableDates = useMemo(() => {
    return new Set(Object.keys(slotsByDate));
  }, [slotsByDate]);

  const timeSlotsForDate = useMemo(() => {
    if (!selectedDate) return [];
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    return slotsByDate[dateStr] || [];
  }, [selectedDate, slotsByDate]);

  // Duration options
  const durationOptions = useMemo(() => {
    if (!config) return [30];
    const opts = [];
    for (let d = config.min_duration; d <= config.max_duration; d += config.slot_increment) {
      opts.push(d);
    }
    return opts;
  }, [config]);

  const handleBook = async () => {
    if (!selectedSlot || !form.title.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setBooking(true);
    try {
      const res = await axios.post(`${API}/book-meeting`, {
        teacher_id: teacherId,
        title: form.title.trim(),
        description: form.description.trim() || null,
        start_time: selectedSlot.start_datetime,
        duration_minutes: parseInt(duration),
        zoom_link: form.zoom_link.trim() || null,
      });
      setBookingResult(res.data);
      setBookingSuccess(true);
      setConfirmOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to book meeting");
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex justify-center py-24">
          <div className="w-8 h-8 border-4 border-slate-200 border-t-amber-500 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  // Success state
  if (bookingSuccess && bookingResult) {
    return (
      <Layout user={user}>
        <div className="max-w-lg mx-auto text-center py-16" data-testid="booking-success">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="font-serif text-2xl font-bold text-slate-900 mb-2">
            Meeting Request Submitted
          </h2>
          <p className="text-slate-500 mb-1">
            Your meeting request has been sent to {teacher?.name || "your teacher"}.
          </p>
          <p className="text-sm text-slate-400 mb-8">
            You will be notified once it's approved.
          </p>
          <div className="flex justify-center gap-3">
            <Button
              data-testid="view-meeting-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-full"
              onClick={() => navigate(`/meetings/${bookingResult.meeting_id}`)}
            >
              View Meeting
            </Button>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => navigate("/meetings")}
            >
              Back to Meetings
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="book-meeting-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" className="text-slate-500 -ml-2" onClick={() => navigate("/meetings")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="font-serif text-3xl font-bold text-slate-900 tracking-tight">
              Book a Meeting
            </h1>
            {teacher && (
              <div className="flex items-center gap-2 mt-1">
                <Avatar className="h-6 w-6">
                  <AvatarImage src={teacher.picture} />
                  <AvatarFallback className="text-xs bg-slate-100">{teacher.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <span className="text-slate-500">with {teacher.name}</span>
              </div>
            )}
          </div>
        </div>

        {/* Duration selector */}
        {durationOptions.length > 1 && (
          <div className="flex items-center gap-3">
            <Label className="text-xs uppercase tracking-widest text-slate-400 font-bold">Duration</Label>
            <div className="flex gap-2">
              {durationOptions.map((d) => (
                <Button
                  key={d}
                  data-testid={`duration-${d}`}
                  variant={duration === String(d) ? "default" : "outline"}
                  size="sm"
                  className={`rounded-full ${duration === String(d) ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}`}
                  onClick={() => {
                    setDuration(String(d));
                    setSelectedSlot(null);
                  }}
                >
                  {d} min
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar - Date Selection */}
          <Card className="lg:col-span-2 border border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-serif text-lg">Select a Date</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setWeekStart(subWeeks(weekStart, 1))}
                    disabled={isBefore(weekStart, new Date())}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-slate-600 min-w-36 text-center">
                    {format(weekStart, "MMM d")} – {format(addDays(weekStart, 6), "MMM d, yyyy")}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setWeekStart(addWeeks(weekStart, 1))}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-2">
                {weekDays.map((day) => {
                  const dateStr = format(day, "yyyy-MM-dd");
                  const hasSlots = availableDates.has(dateStr);
                  const isSelected = selectedDate && isSameDay(day, selectedDate);
                  const isPast = isBefore(day, new Date()) && !isSameDay(day, new Date());
                  const slotCount = slotsByDate[dateStr]?.length || 0;

                  return (
                    <button
                      key={dateStr}
                      data-testid={`date-${dateStr}`}
                      disabled={!hasSlots || isPast}
                      onClick={() => {
                        setSelectedDate(day);
                        setSelectedSlot(null);
                      }}
                      className={`
                        relative p-3 rounded-xl text-center transition-all duration-200 border-2
                        ${isSelected
                          ? "border-amber-500 bg-amber-50 shadow-md"
                          : hasSlots && !isPast
                            ? "border-transparent bg-white hover:border-amber-300 hover:bg-amber-50/50 cursor-pointer"
                            : "border-transparent bg-slate-50 opacity-50 cursor-not-allowed"
                        }
                      `}
                    >
                      <p className="text-xs text-slate-400 uppercase">{format(day, "EEE")}</p>
                      <p className={`text-xl font-bold mt-1 ${isSelected ? "text-amber-700" : hasSlots ? "text-slate-800" : "text-slate-300"}`}>
                        {format(day, "d")}
                      </p>
                      {hasSlots && !isPast && (
                        <p className="text-xs text-green-600 mt-1">{slotCount} slots</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Time Slots */}
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                {selectedDate ? format(selectedDate, "EEEE, MMM d") : "Select a date"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedDate ? (
                <p className="text-sm text-slate-400 italic text-center py-8">
                  Pick a date to see available times
                </p>
              ) : timeSlotsForDate.length === 0 ? (
                <p className="text-sm text-slate-400 italic text-center py-8">
                  No available slots on this date
                </p>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                  {timeSlotsForDate.map((slot) => {
                    const isSelected = selectedSlot?.start_time === slot.start_time && selectedSlot?.date === slot.date;
                    return (
                      <button
                        key={`${slot.date}-${slot.start_time}`}
                        data-testid={`slot-${slot.start_time}`}
                        onClick={() => setSelectedSlot(slot)}
                        className={`
                          w-full px-4 py-3 rounded-xl text-left transition-all duration-200 border-2
                          ${isSelected
                            ? "border-amber-500 bg-amber-50 shadow-sm"
                            : "border-transparent bg-slate-50 hover:border-amber-300 hover:bg-amber-50/50"
                          }
                        `}
                      >
                        <span className={`text-sm font-semibold ${isSelected ? "text-amber-700" : "text-slate-700"}`}>
                          {formatTimeDisplay(slot.start_time)} – {formatTimeDisplay(slot.end_time)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {selectedSlot && (
                <Button
                  data-testid="continue-booking-btn"
                  className="w-full mt-4 bg-amber-600 hover:bg-amber-700 text-white rounded-full"
                  onClick={() => setConfirmOpen(true)}
                >
                  Continue
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Booking Confirmation Dialog */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Confirm Booking</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              {selectedSlot && (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex items-center gap-2 text-sm text-amber-700">
                    <Calendar className="w-4 h-4" />
                    <span className="font-medium">{selectedDate && format(selectedDate, "EEEE, MMMM d, yyyy")}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-amber-700 mt-1">
                    <Clock className="w-4 h-4" />
                    <span>{formatTimeDisplay(selectedSlot.start_time)} – {formatTimeDisplay(selectedSlot.end_time)} ({duration} min)</span>
                  </div>
                </div>
              )}
              <div>
                <Label>Meeting Title *</Label>
                <Input
                  data-testid="booking-title-input"
                  placeholder="e.g., Weekly check-in, Bible study review..."
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div>
                <Label>Description / Agenda</Label>
                <Textarea
                  data-testid="booking-description-input"
                  placeholder="What would you like to discuss?"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div>
                <Label>Zoom Link (optional)</Label>
                <Input
                  data-testid="booking-zoom-input"
                  placeholder="https://zoom.us/j/..."
                  value={form.zoom_link}
                  onChange={(e) => setForm({ ...form, zoom_link: e.target.value })}
                />
              </div>
              <Button
                data-testid="confirm-booking-btn"
                className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-full"
                onClick={handleBook}
                disabled={booking}
              >
                {booking ? "Submitting..." : "Submit Meeting Request"}
              </Button>
              <p className="text-xs text-center text-slate-400">
                Your teacher will be notified and can approve this request.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

function formatTimeDisplay(time) {
  const [h, m] = time.split(":");
  const hour = parseInt(h);
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${m} ${ampm}`;
}
